import { Component, OnInit } from '@angular/core';
import {
  ActivationKeySearchResultRow,
  ActivationKeysExportParams,
  ActivationKeysService,
} from 'src/app/services/activation-keys.service';
import {
  TableSettings,
  TableSpacing,
} from 'src/app/components/pagination/pagination.component';

@Component({
  selector: 'app-activation-keys-page',
  templateUrl: './activation-keys-page.component.html',
  styleUrl: './activation-keys-page.component.scss',
})
export class ActivationKeysPageComponent implements OnInit {
  rows: ActivationKeySearchResultRow[] = [];
  loading = false;
  error = '';

  accountNumber = '';
  createdAfter = '';
  createdBefore = '';
  createdBy = '';

  pageSize = 20;
  currentPage = 1;
  totalCount = 0;
  tableSpacing: TableSpacing = 'normal';
  exporting = false;

  expandedRowIds = new Set<string>();

  private searchQuery: ActivationKeysExportParams = {};

  constructor(private activationKeysService: ActivationKeysService) {}

  ngOnInit() {
    this.initializeDateFilters();
    this.loadSettings();
    this.fetch();
  }

  private initializeDateFilters() {
    const today = new Date();
    const previousMonth = new Date(
      today.getFullYear(),
      today.getMonth() - 1,
      Math.min(
        today.getDate(),
        new Date(today.getFullYear(), today.getMonth(), 0).getDate()
      )
    );
    this.createdAfter = this.formatDateInput(previousMonth);
    this.createdBefore = this.formatDateInput(today);
  }

  private formatDateInput(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  toggleRow(id: string) {
    if (this.expandedRowIds.has(id)) {
      this.expandedRowIds.delete(id);
    } else {
      this.expandedRowIds.add(id);
    }
  }

  get totalPages() {
    if (this.totalCount === 0) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.totalCount / this.pageSize));
  }

  loadSettings() {
    const raw = localStorage.getItem('activationKeysTableSettings');
    if (!raw) {
      return;
    }
    try {
      const parsed = JSON.parse(raw) as TableSettings;
      if (typeof parsed.pageSize === 'number' && parsed.pageSize > 0) {
        this.pageSize = parsed.pageSize;
      }
      if (parsed.spacing) {
        this.tableSpacing = parsed.spacing;
      }
    } catch {
      /* ignore */
    }
  }

  saveSettings(settings: TableSettings) {
    const sizeChanged = settings.pageSize !== this.pageSize;
    this.pageSize = settings.pageSize;
    this.tableSpacing = settings.spacing;
    localStorage.setItem(
      'activationKeysTableSettings',
      JSON.stringify(settings)
    );
    if (sizeChanged) {
      this.currentPage = 1;
    }
  }

  search() {
    this.currentPage = 1;
    this.fetch();
  }

  clearFilters() {
    this.accountNumber = '';
    this.createdAfter = '';
    this.createdBefore = '';
    this.createdBy = '';
    this.currentPage = 1;
    this.fetch();
  }

  onPageChanged(page: number) {
    this.currentPage = page;
    this.fetch();
  }

  fetch() {
    this.loading = true;
    this.error = '';
    this.searchQuery = {
      accountNumber: this.accountNumber || undefined,
      createdAfter: this.createdAfter || undefined,
      createdBefore: this.createdBefore || undefined,
      createdBy: this.createdBy || undefined,
    };
    this.activationKeysService
      .search({
        pageNumber: this.currentPage,
        pageSize: this.pageSize,
        ...this.searchQuery,
      })
      .subscribe({
        next: (res) => {
          this.loading = false;
          this.rows = res.rows;
          this.totalCount = res.total_count;
        },
        error: () => {
          this.loading = false;
          this.error = 'Could not load activation keys.';
        },
      });
  }

  exportCsv() {
    if (this.exporting || this.totalCount === 0) {
      return;
    }
    this.exporting = true;
    this.error = '';
    this.activationKeysService.exportSearch(this.searchQuery).subscribe({
      next: (blob) => {
        this.exporting = false;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const today = new Date().toISOString().slice(0, 10);
        link.href = url;
        link.download = `activation-keys-${today}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      },
      error: () => {
        this.exporting = false;
        this.error = 'Could not export activation keys.';
      },
    });
  }
}
